/**
 * RenderPrerequisites - Validation utilities for render cycle prerequisites
 *
 * Extracted from EdgeDocsCanvas.V3.tsx Phase 4 Step 2
 * 
 * This utility validates that all required dependencies are available
 * before starting a render cycle, preventing wasted work and providing
 * clear diagnostic information when prerequisites are not met.
 */

import type { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';

/**
 * Dependencies required for rendering
 */
export interface RenderPrerequisites {
  canvas: HTMLCanvasElement | null;
  typesetterClient: TypesetterWorkerClient | null;
  isTypesetterReady: boolean;
}

/**
 * Options for prerequisite validation
 */
export interface PrerequisiteValidationOptions {
  enableLogging?: boolean;
  pageIndex?: number;
}

/**
 * Result of prerequisite validation
 */
export interface PrerequisiteValidationResult {
  valid: boolean;
  canvas: HTMLCanvasElement | null;
  ctx: CanvasRenderingContext2D | null;
  reason?: string;
}

/**
 * Validate that all render prerequisites are met
 * 
 * Checks:
 * 1. Canvas element exists
 * 2. Typesetter client is available
 * 3. Typesetter is ready (WASM loaded)
 * 4. Canvas context is obtainable
 *
 * @param deps - Required dependencies for rendering
 * @param options - Validation options (logging, page index)
 * @returns Validation result with canvas/context if valid
 */
export function validateRenderPrerequisites(
  deps: RenderPrerequisites,
  options: PrerequisiteValidationOptions = {}
): PrerequisiteValidationResult {
  const { enableLogging = false, pageIndex } = options;
  const { canvas, typesetterClient, isTypesetterReady } = deps;
  
  const debugLog = enableLogging ? console.log.bind(console) : () => {};
  const pageLabel = pageIndex !== undefined ? `[Page ${pageIndex}]` : '';

  // Check canvas element
  if (!canvas) {
    debugLog(`🎨 [CanvasV3]${pageLabel} Skipping render - no canvas element`);
    return { valid: false, canvas: null, ctx: null, reason: 'no-canvas' };
  }

  // Check typesetter client
  if (!typesetterClient) {
    debugLog(`🎨 [CanvasV3]${pageLabel} Skipping render - no typesetter client`);
    return { valid: false, canvas, ctx: null, reason: 'no-typesetter-client' };
  }

  // Check typesetter ready state
  if (!isTypesetterReady) {
    debugLog(`🎨 [CanvasV3]${pageLabel} Skipping render - typesetter not ready`);
    return { valid: false, canvas, ctx: null, reason: 'typesetter-not-ready' };
  }

  // Get canvas context
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error(`🎨 [CanvasV3]${pageLabel} No canvas context available`);
    return { valid: false, canvas, ctx: null, reason: 'no-canvas-context' };
  }

  // All prerequisites met
  debugLog(`🎨 [CanvasV3]${pageLabel} Prerequisites validated successfully`);
  return { valid: true, canvas, ctx };
}

/**
 * Quick check if prerequisites are met (no context acquisition)
 * 
 * Use this for early bailout checks before doing any work.
 * Does not attempt to get canvas context.
 *
 * @param deps - Required dependencies for rendering
 * @returns true if basic prerequisites are met
 */
export function hasRenderPrerequisites(deps: RenderPrerequisites): boolean {
  const { canvas, typesetterClient, isTypesetterReady } = deps;
  return !!(canvas && typesetterClient && isTypesetterReady);
}
